import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useDtfArtCleanup -- shared read of the SuperAdmin
 * ``dtf_art_cleanup_enabled`` platform switch (Plan A, Jake 2026-10-29): the
 * opt-in "send DTF art for cleanup" through Artistic Punch.
 *
 * Surfaces that only need the switch (a button on a blocked placement) read
 * it here; the Send for digitization dialog gets the switch and its fees from
 * its own sizing read and does not use this.
 *
 * Fail-SAFE default FALSE: ``cleanupEnabled`` reads FALSE while the fetch is
 * in flight, if it errors, and if the payload is not a real boolean. The
 * backend's unset default is FALSE too. A failed read must never reveal an
 * entry point an SA has not enabled. (The server refuses the send itself
 * when the switch is off, so this only decides what is SHOWN.)
 *
 * ``cleanupFee`` is the fee the "Send for art cleanup ($25)" button label
 * shows: the Super Admin ``dtf-art-cleanup-fee-usd`` setting, read only while
 * the switch is ON. NULL until read, on a failed read and on anything that is
 * not a two-place dollar string; a null fee only drops the amount from the
 * label (the send dialog always discloses the fee before anything is sent),
 * it never hides the button or flips the switch.
 *
 * One GET per page load: module-level cache + in-flight dedupe.
 *
 *   const { cleanupEnabled, cleanupFee, loading, error } = useDtfArtCleanup()
 */
const ENDPOINT = '/api/platform-settings/dtf-art-cleanup-enabled'
const FEE_ENDPOINT = '/api/platform-settings/dtf-art-cleanup-fee-usd'
const FEE_RE = /^[0-9]{1,3}\.[0-9]{2}$/

const _value = ref(false)
const _fee = ref(null)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) return Promise.resolve(_value.value)
    if (_inflight) return _inflight
    _loading.value = true
    _error.value = null
    // The leading .then also turns a synchronous throw from the client into
    // the same fail-safe path as a rejected request.
    _inflight = Promise.resolve()
        .then(() => axiosInstance.get(ENDPOINT))
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean overrides the fail-safe FALSE.
            _value.value = typeof raw === 'boolean' ? raw : false
            _resolved = true
            return _value.value
        })
        .then(async (on) => {
            // The fee is a label nicety: its failure must not touch the switch.
            if (!on) return on
            try {
                const resp = await axiosInstance.get(FEE_ENDPOINT)
                const fee = resp && resp.data ? resp.data.value : null
                _fee.value = typeof fee === 'string' && FEE_RE.test(fee) ? fee : null
            } catch (_err) {
                _fee.value = null
            }
            return on
        })
        .catch((err) => {
            // Fail SAFE; not marked resolved so a later mount retries.
            _error.value = err
            _value.value = false
            _fee.value = null
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/** Test-only: reset the module-level shared state. */
export function _resetDtfArtCleanupCache() {
    _value.value = false
    _fee.value = null
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useDtfArtCleanup() {
    _fetchOnce()
    return {
        cleanupEnabled: _value,
        cleanupFee: _fee,
        loading: _loading,
        error: _error,
    }
}

export default useDtfArtCleanup
